import React, { useMemo } from "react";
import NoData from "../../common/NoData";
import useThemeColors from "../../organization/portal/useThemeColors";
import { alphaFromHex } from "../../../util/colors";
import useTranslation from "../../../i18n/useTranslation";

export default function Hockey({ groupName, stageName, standings }) {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const scopeId = useMemo(() => {
        const safe = `${groupName || "group"}-${stageName || "stage"}`
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `kbola-hockey-${safe || "table"}`;
    }, [groupName, stageName]);
    const hasRows = Array.isArray(standings) && standings.length > 0;

    return (
        <div id={scopeId} className="card card-secondary kbola-standingsCard">
            <div className="card-header bg-kbola kbola-hockeyHeader">
                <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                    <span className="kbola-hockeyPill">
                        <i className="fas fa-hockey-puck" />
                        {t("hockeyStandings.pill")}
                    </span>
                    <h3 className="card-title mb-0 kbola-hockeyTitle" title={`${groupName} (${stageName})`}>
                        <b>{groupName} ({stageName})</b>
                    </h3>
                </div>
                <div className="card-tools">
                    <button
                        type="button"
                        className="btn btn-tool kbola-hockeyTool"
                        data-card-widget="collapse"
                        title={t("hockeyStandings.collapse")}
                        aria-label={t("hockeyStandings.collapse")}
                    >
                        <i className="fas fa-minus" />
                    </button>
                </div>
            </div>

            <div className="card-body table-responsive p-0">
                <table className="table table-striped projects mb-0 kbola-hockeyTable">
                    <thead>
                        <tr>
                            <th>{t("hockeyStandings.columns.rank")}</th>
                            <th>{t("hockeyStandings.columns.team")}</th>
                            <th>{t("hockeyStandings.columns.played")}</th>
                            <th>{t("hockeyStandings.columns.win")}</th>
                            <th>{t("hockeyStandings.columns.lose")}</th>
                            <th>{t("hockeyStandings.columns.overtimeLosses")}</th>
                            <th>{t("hockeyStandings.columns.points")}</th>
                            <th>{t("hockeyStandings.columns.goalsFor")}</th>
                            <th>{t("hockeyStandings.columns.goalsAgainst")}</th>
                            <th>{t("hockeyStandings.columns.goalDifference")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hasRows ? standings.map((standing, index) => (
                            <tr key={standing?.team?.id || index}>
                                <td className="kbola-hockeyRank">{index + 1}</td>
                                <td className="kbola-hockeyTeam" title={standing?.team?.name}>{standing?.team?.name}</td>
                                <td>{standing?.played ?? 0}</td>
                                <td>{Number(standing?.win || 0) + Number(standing?.otWin || 0)}</td>
                                <td>{standing?.lose ?? 0}</td>
                                <td>{standing?.otLose ?? 0}</td>
                                <td className="kbola-hockeyPoints">{standing?.points ?? 0}</td>
                                <td>{standing?.scoreFor ?? 0}</td>
                                <td>{standing?.scoreAgainst ?? 0}</td>
                                <td>{standing?.scoreDifference ?? 0}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={10}>
                                    <NoData />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
                #${scopeId} {
                    border: 1px solid rgba(0,0,0,.08);
                    border-radius: 18px;
                    box-shadow: 0 18px 44px rgba(0,0,0,.10);
                    font-family: 'Poppins', sans-serif;
                    margin-bottom: 14px;
                    overflow: hidden;
                }
                #${scopeId} .kbola-hockeyHeader {
                    align-items: center;
                    background: linear-gradient(135deg, ${alphaFromHex(colors.primary, 1)}, ${alphaFromHex(colors.primary, .72)});
                    display: flex;
                    justify-content: space-between;
                    padding: 12px 14px;
                }
                #${scopeId} .kbola-hockeyPill {
                    align-items: center;
                    background: ${colors.secondary};
                    border: 1px solid rgba(0,0,0,.12);
                    border-radius: 999px;
                    color: ${colors.textOnSecondary};
                    display: inline-flex;
                    font-size: 11.5px;
                    font-weight: 900;
                    gap: 8px;
                    padding: 6px 10px;
                    white-space: nowrap;
                }
                #${scopeId} .kbola-hockeyTitle {
                    color: ${colors.textOnPrimary};
                    font-size: 14px;
                    font-weight: 900;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                #${scopeId} .kbola-hockeyTool {
                    align-items: center;
                    background: ${colors.primary};
                    border: 1px solid rgba(0,0,0,.12);
                    border-radius: 12px;
                    color: ${colors.textOnPrimary};
                    display: inline-flex;
                    height: 38px;
                    justify-content: center;
                    margin-left: 10px;
                    padding: 0;
                    width: 38px;
                }
                #${scopeId} .card-tools {
                    align-items: center;
                    display: flex;
                    flex: 0 0 auto;
                    justify-content: flex-end;
                    margin-left: auto;
                }
                #${scopeId} .kbola-hockeyTable { margin: 0; min-width: 700px; }
                #${scopeId} .kbola-hockeyTable th {
                    background: ${alphaFromHex(colors.primary, .12)};
                    border-bottom: 1px solid rgba(0,0,0,.08);
                    color: rgba(20,21,26,.72);
                    font-size: 11.5px;
                    font-weight: 900;
                    letter-spacing: .02em;
                    padding: 12px 10px;
                    text-align: center;
                    text-transform: uppercase;
                    white-space: nowrap;
                }
                #${scopeId} .kbola-hockeyTable th:nth-child(2),
                #${scopeId} .kbola-hockeyTable td:nth-child(2) { text-align: left; }
                #${scopeId} .kbola-hockeyTable td {
                    border-top: 1px solid rgba(0,0,0,.06);
                    color: rgba(20,21,26,.86);
                    font-size: 13px;
                    font-weight: 700;
                    padding: 11px 10px;
                    text-align: center;
                    vertical-align: middle;
                }
                #${scopeId} .kbola-hockeyTable tbody tr:hover { background: ${alphaFromHex(colors.primary, .08)}; }
                #${scopeId} .kbola-hockeyTable .kbola-hockeyRank { color: rgba(20,21,26,.58); font-weight: 800; }
                #${scopeId} .kbola-hockeyTable .kbola-hockeyTeam { font-weight: 800; max-width: 220px; min-width: 150px; }
                #${scopeId} .kbola-hockeyTable .kbola-hockeyPoints { font-size: 14px; font-weight: 900; }
                @media (max-width: 576px) {
                    #${scopeId} .kbola-hockeyTitle { max-width: 40vw; }
                }
            `}</style>
        </div>
    );
}
